import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, Time, Util} from 'yougile-util'
import {
  DataObjectId,
  ObjectType,
  TimelineFilter,
  TimelineFilterBackend,
  UserEventsByDay,
  UserEventsByTime
} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {
  UserEventsClient,
  UserEventsClientDataLimited
} from '@client/user-events-client'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {GanttDraft} from '@extensions/gantt-draft'
import {globals} from '@extensions/globals-base'
import {
  createEmptyFilter,
  eventsGroupByDay,
  filterEvents,
  filterLocationStats,
  getActiveFilter,
  getFiltersForBackend
} from './utils/utils'

const itemsToLoad = 100
const itemsToLoadXLSX = 1000
const maxLoadAttempts = 20
export const dateFormat = 'dd.MM.yyyy'
export const dateWithTimeFormat = 'dd.MM.yyyy HH:mm'

type ToggleView = 'table' | 'card'

interface State {
  start: number
  stop: number
  stats: UserEventsClientDataLimited
  statsXLSX: UserEventsClientDataLimited
  view: ToggleView
  errors: Set<number>
  showFilter: boolean
  filter: TimelineFilter
  itemsToLoad: number
  itemsToLoadLocation: number
  isFetching: boolean
  isEventsLeft: boolean
  isLoadingXLSX: boolean
  isLoadingEvents: boolean
}

export class StatsTimelineStore {
  state: State = {
    start: Time.now(),
    stop: Time.now(),
    stats: null,
    statsXLSX: null,
    view: 'card',
    errors: new Set(),
    showFilter: false,
    filter: {...createEmptyFilter(), id: Util.longId()},
    itemsToLoad: itemsToLoad,
    itemsToLoadLocation: itemsToLoad,
    isFetching: false,
    isEventsLeft: null,
    isLoadingXLSX: false,
    isLoadingEvents: false
  }

  constructor() {
    makeAutoObservable(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )
    const dateFrom = new Date().setDate(new Date().getDate() - 90)
    const dateTo = new Date().getTime()
    this.state.start = Time.startOfDay(dateFrom)
    this.state.stop = Time.endOfDay(dateTo)
    if (!this.profileHub.data.eventFeedFilters) {
      this.profileHub.data.eventFeedFilters = [
        {
          ...createEmptyFilter(),
          action: ['delete'],
          title: Lang.msg.statsTimeline.deletedObjects,
          id: Util.longId()
        },
        {
          ...createEmptyFilter(),
          action: ['file'],
          title: Lang.msg.statsTimeline.uploadedFiles,
          id: Util.longId()
        },
        {
          ...createEmptyFilter(),
          action: ['message'],
          title: Lang.msg.statsTimeline.communicationByTasks,
          id: Util.longId()
        }
      ]
      this.profileHub.commit()
    }
  }

  async updateStats() {
    await this.updateStatsImpl(
      stats => this.setStats(stats),
      () => this.events,
      () => this.state.itemsToLoad,
      () => this.filtersForBackend,
      () => this.loadMore()
    )
  }

  async updateStatsImpl(
    setStats: (stats: UserEventsClientDataLimited) => void,
    getFilteredEvents: () => UserEventsByTime | null,
    getItemsToLoad: () => number,
    getFiltersForBackend: () => TimelineFilterBackend,
    loadMore: () => void
  ) {
    if (this.state.isLoadingEvents) {
      logger.log('[Events] Stats update skipped: events are already loading')
      return
    }
    logger.log('[Events] Starting to load events')
    this.state.isLoadingEvents = true
    try {
      const targetEventsCount = getItemsToLoad()
      logger.log('[Events] Target events count:', targetEventsCount)
      let attempts = 0
      while (
        Object.keys(getFilteredEvents() ?? {}).length < targetEventsCount &&
        attempts < maxLoadAttempts
      ) {
        logger.log('[Events] Loading attempt:', attempts + 1)
        if (attempts > 0) {
          logger.log('[Events] Loading more events')
          loadMore()
        }
        attempts++
        logger.log('[Events] Fetching data from UserEventsClient')
        const stats = await UserEventsClient.getDataLimited(
          globals.getCompanyId(),
          getItemsToLoad(),
          getFiltersForBackend()
        )
        logger.log(
          '[Events] Received stats data, events count:',
          Object.keys(stats.events).length
        )
        setStats(stats)
        if (!stats.isEventsLeft) {
          logger.log('[Events] No more events left to load')
          break
        }
      }
      logger.log(
        '[Events] Events loaded:',
        Object.keys(getFilteredEvents() ?? {}).length
      )
      logger.log('[Events] Finished loading events after', attempts, 'attempts')
    } catch (error) {
      logger.error('[Events] Error updating stats:', error)
      throw error
    } finally {
      logger.log('[Events] Resetting isLoadingEvents flag')
      this.state.isLoadingEvents = false
    }
  }

  setStats(result: UserEventsClientDataLimited) {
    const isLocationFilter = this.state.filter?.located?.length > 0
    this.state.stats = result
    this.state.isFetching = false
    this.state.isEventsLeft = isLocationFilter
      ? Object.keys(result.events).length > this.state.itemsToLoadLocation
      : result.isEventsLeft
  }

  async updateStatsXLSX() {
    if (this.state.isLoadingXLSX) {
      return
    }
    this.state.isLoadingXLSX = true
    let loadCount = itemsToLoadXLSX
    await this.updateStatsImpl(
      stats => this.setStatsXLSX(stats),
      () => this.eventsXLSX,
      () => loadCount,
      () => this.filtersForBackend,
      () => (loadCount += itemsToLoad)
    )
    this.state.isLoadingXLSX = false
  }

  setStatsXLSX(result: UserEventsClientDataLimited) {
    this.state.statsXLSX = result
  }

  saveNewFilter(filter: TimelineFilter) {
    if (this.profileHub.data.eventFeedFilters.length > 0) {
      const data = [...this.profileHub.data.eventFeedFilters, filter]
      this.profileHub.data.eventFeedFilters = data
      this.profileHub.commit()
    } else {
      this.profileHub.data.eventFeedFilters = [filter]
      this.profileHub.commit()
    }
    this.setActiveFilter(filter.id)
  }

  updateCurrentFilter(filter: TimelineFilter) {
    const data = this.profileHub.data.eventFeedFilters.map(el =>
      el.id === filter.id ? {...el, ...filter} : el
    )
    this.profileHub.data.eventFeedFilters = data
    this.profileHub.commit()
    this.setActiveFilter(filter.id)
  }

  deleteCurrentFilter(id: TimelineFilter['id']) {
    const data = this.profileHub.data.eventFeedFilters.filter(f => f.id !== id)
    this.profileHub.data.eventFeedFilters = data
    this.profileHub.commit()
    this.state.filter = {...createEmptyFilter(), id: Util.longId()}
  }

  saveFromCurrentAsNewFilter(filter: TimelineFilter) {
    const data = [...this.profileHub.data.eventFeedFilters, filter]
    this.profileHub.data.eventFeedFilters = data
    this.profileHub.commit()
    this.setActiveFilter(filter.id)
  }

  deleteAllFilter() {
    this.profileHub.data.eventFeedFilters = null
    this.profileHub.commit()
  }

  resetFilter() {
    this.state.filter = {...createEmptyFilter(), id: Util.longId()}
  }

  setActiveFilter(id: TimelineFilter['id']) {
    this.state.filter = this.profileHub.data.eventFeedFilters.find(
      f => f.id === id
    )
  }

  setActiveFilterForce(filter: TimelineFilter) {
    this.state.filter = filter
  }

  get profileHub() {
    const profile = Sync.getProfile()
    return profile.getHub(profile.companyId)
  }

  get filteredStats() {
    if (this.state.filter?.located?.length > 0 && this.activeFilter.length) {
      const {filteredStats, isEventsLeft} = filterLocationStats({
        filter: this.state.filter,
        stats: this.state.stats,
        itemsToLoadLocation: this.state.itemsToLoadLocation
      })
      this.state.isEventsLeft = isEventsLeft
      return filteredStats
    }
    return this.state.stats
  }

  get filteredStatsXLSX() {
    if (this.state.filter?.located?.length > 0 && this.activeFilter.length) {
      const {filteredStats} = filterLocationStats({
        filter: this.state.filter,
        stats: this.state.statsXLSX,
        itemsToLoadLocation: itemsToLoadXLSX
      })
      return filteredStats
    }
    return this.state.statsXLSX
  }

  get activeFilter(): Array<keyof TimelineFilter> {
    if (!this.state.filter) {
      return []
    }
    this.resetItemsToLoad()
    return getActiveFilter(this.state.filter)
  }

  get filtersForBackend(): TimelineFilterBackend {
    return getFiltersForBackend(this.state.filter, this.activeFilter)
  }

  get events(): UserEventsByTime {
    if (!this.state.stats) {
      return {}
    }
    const {events} = this.filteredStats
    return filterEvents(events)
  }

  get eventsXLSX(): UserEventsByTime {
    if (!this.state.statsXLSX) {
      return null
    }
    const {events} = this.filteredStatsXLSX
    return filterEvents(events)
  }

  get eventsByDay(): UserEventsByDay {
    if (!this.events) {
      return {}
    }
    return eventsGroupByDay(this.events)
  }

  restore(objId: DataObjectId, objType: ObjectType) {
    const box = Sync.getCurrentSandbox()
    const hub = box.getHub(objId)
    if (hub) {
      if (PermissionsChecker.checkRename(hub)) {
        box.restore(hub)
        GanttDraft.planEntitySubtree(hub.id, box)
        MessageBox.show(
          Lang.findPartsMsg(
            'statsTimeline',
            `restored${Util.capitalize(objType)}`
          ),
          'success'
        )
      } else {
        MessageBox.warn(Lang.msg.statsTimeline.dissallowedToRestore)
      }
    }
  }

  toggleFilter(): void {
    this.state.showFilter = !this.state.showFilter
  }

  toggleView(view: ToggleView): void {
    this.state.view = view
  }

  reportError(timestamp: number): void {
    logger.warn('[Events] Error with event item', this.events[timestamp])
    this.state.errors.add(timestamp)
  }

  loadMore(): void {
    this.state.itemsToLoad = this.state.itemsToLoad + itemsToLoad
    this.state.isFetching = true
  }

  loadMoreLocation(): void {
    this.state.itemsToLoadLocation =
      this.state.itemsToLoadLocation + itemsToLoad
  }

  resetItemsToLoad(): void {
    this.state.stats = null
    this.state.isFetching = true
    this.state.itemsToLoad = itemsToLoad
    this.state.itemsToLoadLocation = itemsToLoad
  }
}

const [StatsTimelineContext, useStatsTimelineStore] =
  createUseStore(StatsTimelineStore)
export {StatsTimelineContext, useStatsTimelineStore}
